/**
 * Modo Coming Soon — front-end primeiro.
 * O painel grava em `localStorage` e sincroniza um cookie para o middleware.
 * Para deploy sem painel: `COMING_SOON_ENABLED=true` no ambiente.
 */

export type ComingSoonConfig = {
  enabled: boolean;
  title: string;
  description: string;
  /** Texto opcional (ex.: "Lançamento previsto para 2026"). */
  estimatedLaunch: string;
  /** Percentagem exibida na barra de progresso (0–100). */
  progress: number;
  /** Subtítulo abaixo do título da barra de progresso. */
  progressStatus: string;
};

export const COMING_SOON_STORAGE_KEY = 'site:coming-soon:v1';
export const COMING_SOON_COOKIE_NAME = 'xat_coming_soon';

export const COMING_SOON_DEFAULTS: ComingSoonConfig = {
  enabled: false,
  title: 'O futuro começa agora',
  description: `<p>Estamos preparando a maior atualização da história do <span class="whitespace-nowrap">xat&nbsp;Ajuda</span>.</p>
<p>Uma plataforma totalmente redesenhada, pensada para toda a comunidade:</p>
<ul>
<li>Novos guias</li>
<li>Busca inteligente</li>
<li>Powerpedia</li>
<li>Documentação oficial</li>
</ul>`,
  estimatedLaunch: '',
  progress: 90,
  progressStatus: 'Ajustes finais e testes internos',
};

function loadFromStorage(): Partial<ComingSoonConfig> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COMING_SOON_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ComingSoonConfig>;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Config efectiva (defaults + override local do painel). */
export function getComingSoonConfig(): ComingSoonConfig {
  const patch = loadFromStorage();
  return { ...COMING_SOON_DEFAULTS, ...patch };
}

/** Persiste alterações e sincroniza cookie para o middleware. */
export function saveComingSoonConfig(patch: Partial<ComingSoonConfig>): ComingSoonConfig {
  const next = { ...getComingSoonConfig(), ...patch };
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(COMING_SOON_STORAGE_KEY, JSON.stringify(next));
    syncComingSoonCookie(next.enabled);
  }
  return next;
}

export function resetComingSoonConfig(): ComingSoonConfig {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(COMING_SOON_STORAGE_KEY);
    syncComingSoonCookie(false);
  }
  return { ...COMING_SOON_DEFAULTS };
}

/** Cookie lido pelo middleware (`1` = activo, `0` = inactivo). */
export function syncComingSoonCookie(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  const value = enabled ? '1' : '0';
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `${COMING_SOON_COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function isComingSoonOverridden(): boolean {
  return loadFromStorage() !== null;
}

/** Detecta descrição em HTML (painel com rich text). */
export function isComingSoonDescriptionHtml(text: string): boolean {
  return /<[a-z][\s\S]*>/i.test(text.trim());
}
